import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { Keyboard, KeyboardResize } from '@capacitor/keyboard'

interface KeyboardManagerOptions {
    /**
     * Array of route patterns to disable keyboard handling on.
     * Supports exact matches and wildcards using asterisk.
     * Examples: /group/asterisk/invite, /account/settings
     */
    disabledRoutes?: string[]
}

/**
 * Global keyboard manager hook that tracks keyboard visibility and height.
 * Updates CSS custom properties on :root for use throughout the app.
 * 
 * CSS Variables set:
 * - --keyboard-height: Height of the keyboard in pixels
 * - --keyboard-visible: 0 or 1 (useful for conditional styling)
 * 
 * Initialize once at the root of your app.
 * 
 * @param options Configuration options including disabled routes
 */
export function useKeyboardManager(options: KeyboardManagerOptions = {}) {
    const { disabledRoutes = [] } = options
    const location = useLocation()
    const [keyboardHeight, setKeyboardHeight] = useState(0)
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)

    // Check if current route is disabled
    const isDisabled = disabledRoutes.some(pattern => {
        // Convert wildcard pattern to regex
        const regexPattern = pattern
            .replace(/\//g, '\\/')  // Escape forward slashes
            .replace(/\*/g, '[^/]+') // Replace * with non-slash matcher
        const regex = new RegExp(`^${regexPattern}$`)
        return regex.test(location.pathname)
    })

    useEffect(() => {
        let removeShowListener: (() => void) | null = null
        let removeHideListener: (() => void) | null = null

        const setupKeyboardListeners = async () => {
            if (Capacitor.isNativePlatform()) {
                // iOS-specific configuration
                if (Capacitor.getPlatform() === 'ios') {
                    try {
                        // Hide the accessory bar for a cleaner look
                        await Keyboard.setAccessoryBarVisible({ isVisible: false })

                        // Prevent iOS from resizing the webview - we'll handle positioning manually
                        await Keyboard.setResizeMode({ mode: KeyboardResize.None })

                        // Allow Capacitor to auto-scroll inputs into view
                        await Keyboard.setScroll({ isDisabled: false })
                    } catch (error) {
                        console.warn('Failed to configure keyboard settings:', error)
                    }
                }

                // Listen for keyboard show events
                const showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
                    // Set keyboard height to 0 on disabled routes (prevents footer transform)
                    setKeyboardHeight(isDisabled ? 0 : info.keyboardHeight)
                    setIsKeyboardVisible(!isDisabled)

                    // Scroll the focused input into view within its scroll container
                    // This happens even on disabled routes for better UX
                    setTimeout(() => {
                        const activeElement = document.activeElement as HTMLElement

                        if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
                            // Check if element is inside a dialog modal
                            const dialog = activeElement.closest('dialog')

                            // Find the scrollable parent, starting from the input
                            // If inside a dialog, we need to find scroll containers within that dialog
                            let scrollContainer = activeElement.parentElement
                            while (scrollContainer) {
                                // Stop at dialog boundary - don't go outside it
                                if (scrollContainer === dialog?.parentElement) {
                                    scrollContainer = null
                                    break
                                }
                                const overflow = window.getComputedStyle(scrollContainer).overflowY
                                if (overflow === 'auto' || overflow === 'scroll') {
                                    break
                                }
                                scrollContainer = scrollContainer.parentElement
                            }

                            // For dialogs, also check for scroll-container class if not found
                            if (!scrollContainer && dialog) {
                                scrollContainer = dialog.querySelector('.scroll-container') as HTMLElement
                                // Fallback to the inner dialog content card
                                if (!scrollContainer) {
                                    scrollContainer = dialog.querySelector('.temp-card') as HTMLElement
                                }
                            }

                            if (scrollContainer) {
                                const canScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight
                                const elementRect = activeElement.getBoundingClientRect()
                                const containerRect = scrollContainer.getBoundingClientRect()

                                // On disabled routes, footer doesn't shift so don't account for it
                                const footerHeight = isDisabled ? 0 : 100
                                const keyboardTop = window.innerHeight - info.keyboardHeight
                                const padding = 30

                                // For dialogs, the safe bottom is relative to the dialog/container
                                // rather than the full window
                                const effectiveBottom = dialog
                                    ? Math.min(containerRect.bottom, keyboardTop)
                                    : keyboardTop - footerHeight

                                // Calculate the safe area where input should be visible
                                const safeBottom = effectiveBottom - padding

                                // Check if the input (with padding) is being covered by keyboard/footer
                                const inputBottom = elementRect.bottom + padding

                                // Only scroll UP if input is covered at the bottom
                                if (inputBottom > safeBottom) {
                                    const scrollAmount = inputBottom - safeBottom + 20

                                    // If the container can't scroll, use scrollIntoView on the element itself
                                    if (!canScroll) {
                                        activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                    } else {
                                        scrollContainer.scrollBy({
                                            top: scrollAmount,
                                            behavior: 'smooth'
                                        })
                                    }
                                }
                            } else {
                                // Fallback to scrollIntoView if no scroll container found
                                activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            }
                        }
                    }, 150) // Small delay to let CSS animations start
                })
                removeShowListener = () => showListener.remove()

                // Listen for keyboard hide events
                const hideListener = await Keyboard.addListener('keyboardWillHide', () => {
                    setKeyboardHeight(0)
                    setIsKeyboardVisible(false)

                    // Reset scroll position when keyboard closes
                    setTimeout(() => {
                        // Find all scrollable containers and reset them
                        const scrollContainers = document.querySelectorAll('.column-content')
                        scrollContainers.forEach((container) => {
                            const element = container as HTMLElement
                            if (element.scrollTop > 0) {
                                element.scrollTo({
                                    top: 0,
                                    behavior: 'smooth'
                                })
                            }
                        })
                    }, 250) // Longer delay to ensure keyboard animation completes
                })
                removeHideListener = () => hideListener.remove()
            } else {
                // Web fallback using Visual Viewport API
                const visualViewport = window.visualViewport

                if (visualViewport) {
                    const handleResize = () => {
                        const viewportHeight = visualViewport.height
                        const windowHeight = window.innerHeight

                        // Estimate keyboard height based on viewport resize
                        const estimatedKeyboardHeight = windowHeight - viewportHeight

                        // Use a threshold to determine if keyboard is actually visible
                        // (viewport can resize for other reasons like browser chrome)
                        if (estimatedKeyboardHeight > 100) {
                            setKeyboardHeight(estimatedKeyboardHeight)
                            setIsKeyboardVisible(true)
                        } else {
                            setKeyboardHeight(0)
                            setIsKeyboardVisible(false)
                        }
                    }

                    visualViewport.addEventListener('resize', handleResize)

                    // Cleanup function for web
                    removeShowListener = () => {
                        visualViewport.removeEventListener('resize', handleResize)
                    }
                }
            }
        }

        setupKeyboardListeners()

        // Cleanup
        return () => {
            if (removeShowListener) removeShowListener()
            if (removeHideListener) removeHideListener()
        }
    }, [isDisabled])

    // Update CSS variables whenever keyboard state changes
    useEffect(() => {
        const root = document.documentElement

        root.style.setProperty('--keyboard-height', `${keyboardHeight}px`)
        root.style.setProperty('--keyboard-visible', isKeyboardVisible ? '1' : '0')
    }, [keyboardHeight, isKeyboardVisible])

    return { keyboardHeight, isKeyboardVisible }
}
